import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { validateFacturaForm } from '../Validations/ValidationsFactura';

export default function FacturaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [factura, setFactura] = useState({
    ejercicioId: '',
    fechaFactura: '',
    codigo: '',
    fechaRegistro: '',
    numero: '',
    descripcion: '',
    totalConIva: '',
    baseImponible: '',
    totalDescuento: '',
    totalIva: '',
    terceroId: '',
    detallesFactura: []
  });
  const [ejercicios, setEjercicios] = useState([]);
  const [terceros, setTerceros] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchEjercicios();
    fetchTerceros();
    if (id) {
      fetchFactura(id);
    }
  }, [id]);

  const fetchEjercicios = async () => {
    try {
      const response = await axios.get('/ejercicios');
      setEjercicios(response.data);
    } catch (err) {
      console.error('Error fetching ejercicios:', err);
      setError('Error al cargar los ejercicios. Por favor, recargue la página.');
    }
  };

  const fetchTerceros = async () => {
    try {
      const response = await axios.get('/terceros');
      setTerceros(response.data);
    } catch (err) {
      console.error('Error fetching terceros:', err);
      setError('Error al cargar los terceros. Por favor, recargue la página.');
    }
  };

  const fetchFactura = async (facturaId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`/facturas/${facturaId}`);
      const facturaData = response.data;
      facturaData.fechaFactura = formatDateForInput(facturaData.fechaFactura);
      facturaData.fechaRegistro = formatDateForInput(facturaData.fechaRegistro);
      setFactura(facturaData);
    } catch (err) {
      setError('Error al cargar la factura. Por favor, intente de nuevo.');
      console.error('Error fetching factura:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFactura({ ...factura, [name]: value });
    setValidationErrors({ ...validationErrors, [name]: '' });
  };

  const handleDetalleChange = (index, field, value) => {
    const updatedDetalles = [...factura.detallesFactura];
    updatedDetalles[index] = { ...updatedDetalles[index], [field]: value };
    setFactura({ ...factura, detallesFactura: updatedDetalles });
    setValidationErrors({ ...validationErrors, [`detallesFactura[${index}].${field}`]: '' });
  };

  const addDetalleFactura = () => {
    setFactura({
      ...factura,
      detallesFactura: [
        ...factura.detallesFactura,
        {
          idLinea: factura.detallesFactura.length + 1,
          descripcion: '',
          cantidadProducto: 0,
          iva: 0,
          descuento: 0,
          importe: 0,
          totalEjercicio: 0
        }
      ]
    });
  };

  const removeDetalleFactura = (index) => {
    const updatedDetalles = factura.detallesFactura.filter((_, i) => i !== index);
    setFactura({ ...factura, detallesFactura: updatedDetalles });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setValidationErrors({});

    const facturaData = {
      ...factura,
      ejercicioId: parseInt(factura.ejercicioId),
      fechaFactura: factura.fechaFactura,
      fechaRegistro: factura.fechaRegistro,
      totalConIva: parseFloat(factura.totalConIva),
      baseImponible: parseFloat(factura.baseImponible),
      totalDescuento: parseFloat(factura.totalDescuento),
      totalIva: parseFloat(factura.totalIva),
      detallesFactura: factura.detallesFactura.map((detalle, index) => ({
        ...detalle,
        idLinea: index + 1,
        cantidadProducto: parseFloat(detalle.cantidadProducto),
        iva: parseFloat(detalle.iva),
        descuento: parseFloat(detalle.descuento),
        importe: parseFloat(detalle.importe),
        totalEjercicio: parseFloat(detalle.totalEjercicio)
      }))
    };

    const errors = await validateFacturaForm(facturaData);
    if (Object.keys(errors).length === 0) {
      try {
        if (id) {
          await axios.put(`/facturas/${id}`, facturaData);
        } else {
          await axios.post('/facturas', facturaData);
        }
        navigate('/home/facturas');
      } catch (err) {
        if (err.response && err.response.data) {
          const errorMessage = err.response.data.message;
          if (Array.isArray(errorMessage)) {
            setError(`Error al guardar la factura: ${errorMessage.join(', ')}`);
          } else if (typeof errorMessage === 'string') {
            setError(`Error al guardar la factura: ${errorMessage}`);
          } else {
            setError('Error al guardar la factura. Por favor, verifique los datos e intente de nuevo.');
          }
        } else {
          setError('Error al guardar la factura. Por favor, verifique los datos e intente de nuevo.');
        }
        console.error('Error saving factura:', err);
      }
    } else {
      setValidationErrors(errors);
      setError('Por favor, corrija los errores en el formulario.');
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return <div className="text-center mt-8">Cargando...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold mb-4">{id ? 'Editar' : 'Crear'} Factura</h2>
      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="ejercicioId" className="block mb-1">Ejercicio</label>
          <select
            id="ejercicioId"
            name="ejercicioId"
            value={factura.ejercicioId}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${validationErrors.ejercicioId ? 'border-red-500' : ''}`}
          >
            <option value="">Seleccione un ejercicio</option>
            {ejercicios.map(ejercicio => (
              <option key={ejercicio.id} value={ejercicio.id}>{ejercicio.anio}</option>
            ))}
          </select>
          {validationErrors.ejercicioId && (
            <p className="text-red-500 text-xs italic">{validationErrors.ejercicioId}</p>
          )}
        </div>
        <div>
          <label htmlFor="fechaFactura" className="block mb-1">Fecha Factura</label>
          <input
            type="date"
            id="fechaFactura"
            name="fechaFactura"
            value={factura.fechaFactura}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${validationErrors.fechaFactura ? 'border-red-500' : ''}`}
          />
          {validationErrors.fechaFactura && (
            <p className="text-red-500 text-xs italic">{validationErrors.fechaFactura}</p>
          )}
        </div>
        <div>
          <label htmlFor="codigo" className="block mb-1">Código</label>
          <input
            type="text"
            id="codigo"
            name="codigo"
            value={factura.codigo}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${validationErrors.codigo ? 'border-red-500' : ''}`}
          />
          {validationErrors.codigo && (
            <p className="text-red-500 text-xs italic">{validationErrors.codigo}</p>
          )}
        </div>
        <div>
          <label htmlFor="fechaRegistro" className="block mb-1">Fecha Registro</label>
          <input
            type="date"
            id="fechaRegistro"
            name="fechaRegistro"
            value={factura.fechaRegistro}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${validationErrors.fechaRegistro ? 'border-red-500' : ''}`}
          />
          {validationErrors.fechaRegistro && (
            <p className="text-red-500 text-xs italic">{validationErrors.fechaRegistro}</p>
          )}
        </div>
        <div>
          <label htmlFor="numero" className="block mb-1">Número</label>
          <input
            type="text"
            id="numero"
            name="numero"
            value={factura.numero}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${validationErrors.numero ? 'border-red-500' : ''}`}
          />
          {validationErrors.numero && (
            <p className="text-red-500 text-xs italic">{validationErrors.numero}</p>
          )}
        </div>
        <div>
          <label htmlFor="descripcion" className="block mb-1">Descripción</label>
          <textarea
            id="descripcion"
            name="descripcion"
            value={factura.descripcion}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${validationErrors.descripcion ? 'border-red-500' : ''}`}
          />
          {validationErrors.descripcion && (
            <p className="text-red-500 text-xs italic">{validationErrors.descripcion}</p>
          )}
        </div>
        <div>
          <label htmlFor="totalConIva" className="block mb-1">Total con IVA</label>
          <div className="relative">
            <input
              type="number"
              id="totalConIva"
              name="totalConIva"
              value={factura.totalConIva}
              onChange={handleChange}
              className={`w-full p-2 border rounded pr-8 ${validationErrors.totalConIva ? 'border-red-500' : ''}`}
              step="0.01"
            />
            <span className="absolute right-2 top-2">€</span>
          </div>
          {validationErrors.totalConIva && (
            <p className="text-red-500 text-xs italic">{validationErrors.totalConIva}</p>
          )}
        </div>
        <div>
          <label htmlFor="baseImponible" className="block mb-1">Base Imponible</label>
          <div className="relative">
            <input
              type="number"
              id="baseImponible"
              name="baseImponible"
              value={factura.baseImponible}
              onChange={handleChange}
              className={`w-full p-2 border rounded pr-8 ${validationErrors.baseImponible ? 'border-red-500' : ''}`}
              step="0.01"
            />
            <span className="absolute right-2 top-2">€</span>
          </div>
          {validationErrors.baseImponible && (
            <p className="text-red-500 text-xs italic">{validationErrors.baseImponible}</p>
          )}
        </div>
        <div>
          <label htmlFor="totalDescuento" className="block mb-1">Total Descuento</label>
          <div className="relative">
            <input
              type="number"
              id="totalDescuento"
              name="totalDescuento"
              value={factura.totalDescuento}
              onChange={handleChange}
              className={`w-full p-2 border rounded pr-8 ${validationErrors.totalDescuento ? 'border-red-500' : ''}`}
              step="0.01"
            />
            <span className="absolute right-2 top-2">€</span>
          </div>
          {validationErrors.totalDescuento && (
            <p className="text-red-500 text-xs italic">{validationErrors.totalDescuento}</p>
          )}
        </div>
        <div>
          <label htmlFor="totalIva" className="block mb-1">Total IVA</label>
          <div className="relative">
            <input
              type="number"
              id="totalIva"
              name="totalIva"
              value={factura.totalIva}
              onChange={handleChange}
              className={`w-full p-2 border rounded pr-8 ${validationErrors.totalIva ? 'border-red-500' : ''}`}
              step="0.01"
            />
            <span className="absolute right-2 top-2">€</span>
          </div>
          {validationErrors.totalIva && (
            <p className="text-red-500 text-xs italic">{validationErrors.totalIva}</p>
          )}
        </div>
        <div>
          <label htmlFor="terceroId" className="block mb-1">Tercero</label>
          <select
            id="terceroId"
            
            name="terceroId"
            value={factura.terceroId}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${validationErrors.terceroId ? 'border-red-500' : ''}`}
          >
            <option value="">Seleccione un tercero</option>
            {terceros.map(tercero => (
              <option key={tercero.documento} value={tercero.documento}>{tercero.nombre}</option>
            ))}
          </select>
          {validationErrors.terceroId && (
            <p className="text-red-500 text-xs italic">{validationErrors.terceroId}</p>
          )}
        </div>

        <div>
          <h3 className="text-xl font-semibold mb-2">Detalles de Factura</h3>
          {factura.detallesFactura.map((detalle, index) => (
            <div key={index} className="border p-4 mb-4 rounded">
              <div className="mb-2">
                <label className="block mb-1">ID Línea</label>
                <input
                  type="number"
                  value={detalle.idLinea}
                  onChange={(e) => handleDetalleChange(index, 'idLinea', e.target.value)}
                  className={`w-full p-2 border rounded ${validationErrors[`detallesFactura[${index}].idLinea`] ? 'border-red-500' : ''}`}
                />
                {validationErrors[`detallesFactura[${index}].idLinea`] && (
                  <p className="text-red-500 text-xs italic">{validationErrors[`detallesFactura[${index}].idLinea`]}</p>
                )}
              </div>
              <div className="mb-2">
                <label className="block mb-1">Descripción</label>
                <input
                  type="text"
                  value={detalle.descripcion}
                  onChange={(e) => handleDetalleChange(index, 'descripcion', e.target.value)}
                  className={`w-full p-2 border rounded ${validationErrors[`detallesFactura[${index}].descripcion`] ? 'border-red-500' : ''}`}
                />
                {validationErrors[`detallesFactura[${index}].descripcion`] && (
                  <p className="text-red-500 text-xs italic">{validationErrors[`detallesFactura[${index}].descripcion`]}</p>
                )}
              </div>
              <div className="mb-2">
                <label className="block mb-1">Cantidad</label>
                <input
                  type="number"
                  value={detalle.cantidadProducto}
                  onChange={(e) => handleDetalleChange(index, 'cantidadProducto', e.target.value)}
                  className={`w-full p-2 border rounded ${validationErrors[`detallesFactura[${index}].cantidadProducto`] ? 'border-red-500' : ''}`}
                  step="0.01"
                />
                {validationErrors[`detallesFactura[${index}].cantidadProducto`] && (
                  <p className="text-red-500 text-xs italic">{validationErrors[`detallesFactura[${index}].cantidadProducto`]}</p>
                )}
              </div>
              <div className="mb-2">
                <label className="block mb-1">IVA (%)</label>
                <input
                  type="number"
                  value={detalle.iva}
                  onChange={(e) => handleDetalleChange(index, 'iva', e.target.value)}
                  className={`w-full p-2 border rounded ${validationErrors[`detallesFactura[${index}].iva`] ? 'border-red-500' : ''}`}
                  step="0.01"
                />
                {validationErrors[`detallesFactura[${index}].iva`] && (
                  <p className="text-red-500 text-xs italic">{validationErrors[`detallesFactura[${index}].iva`]}</p>
                )}
              </div>
              <div className="mb-2">
                <label className="block mb-1">Descuento</label>
                <div className="relative">
                  <input
                    type="number"
                    value={detalle.descuento}
                    onChange={(e) => handleDetalleChange(index, 'descuento', e.target.value)}
                    className={`w-full p-2 border rounded pr-8 ${validationErrors[`detallesFactura[${index}].descuento`] ? 'border-red-500' : ''}`}
                    step="0.01"
                  />
                  <span className="absolute right-2 top-2">€</span>
                </div>
                {validationErrors[`detallesFactura[${index}].descuento`] && (
                  <p className="text-red-500 text-xs italic">{validationErrors[`detallesFactura[${index}].descuento`]}</p>
                )}
              </div>
              <div className="mb-2">
                <label className="block mb-1">Importe</label>
                <div className="relative">
                  <input
                    type="number"
                    value={detalle.importe}
                    onChange={(e) => handleDetalleChange(index, 'importe', e.target.value)}
                    className={`w-full p-2 border rounded pr-8 ${validationErrors[`detallesFactura[${index}].importe`] ? 'border-red-500' : ''}`}
                    step="0.01"
                  />
                  <span className="absolute right-2 top-2">€</span>
                </div>
                {validationErrors[`detallesFactura[${index}].importe`] && (
                  <p className="text-red-500 text-xs italic">{validationErrors[`detallesFactura[${index}].importe`]}</p>
                )}
              </div>
              <div className="mb-2">
                <label className="block mb-1">Total Ejercicio</label>
                <div className="relative">
                  <input
                    type="number"
                    value={detalle.totalEjercicio}
                    onChange={(e) => handleDetalleChange(index, 'totalEjercicio', e.target.value)}
                    className={`w-full p-2 border rounded pr-8 ${validationErrors[`detallesFactura[${index}].totalEjercicio`] ? 'border-red-500' : ''}`}
                    step="0.01"
                  />
                  <span className="absolute right-2 top-2">€</span>
                </div>
                {validationErrors[`detallesFactura[${index}].totalEjercicio`] && (
                  <p className="text-red-500 text-xs italic">{validationErrors[`detallesFactura[${index}].totalEjercicio`]}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeDetalleFactura(index)}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Eliminar Detalle
              </button>
            </div>
          ))}
          {validationErrors.detallesFactura && (
            <p className="text-red-500 text-xs italic">{validationErrors.detallesFactura}</p>
          )}
          <button
            type="button"
            onClick={addDetalleFactura}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mb-4"
          >
            Agregar Detalle
          </button>
        </div>

        <button
          type="submit"
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          disabled={isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar'}
        </button>
      </form>
    </div>
  );
}